import type { FileHandleRef, PlatformBridge } from '@slate/core';
import type { OpenDoc } from '../store/docStore';
import type { Hit } from './types';

const PER_DOC_HIT_CAP = 50;
const SNIPPET_PAD = 32;

function snippetAt(line: string, idx: number, qLen: number): { snippet: string; range: [number, number] } {
  const start = Math.max(0, idx - SNIPPET_PAD);
  const end = Math.min(line.length, idx + qLen + SNIPPET_PAD);
  const sliced = line.slice(start, end);
  return { snippet: sliced, range: [idx - start, idx - start + qLen] };
}

function scanContent(doc: OpenDoc, query: string, hits: Hit[]): void {
  if (doc.kind === 'image') return;
  const q = query.toLowerCase();
  const lines = doc.text.split('\n');
  let found = 0;
  for (let i = 0; i < lines.length && found < PER_DOC_HIT_CAP; i++) {
    const line = lines[i];
    const idx = line.toLowerCase().indexOf(q);
    if (idx < 0) continue;
    const { snippet, range } = snippetAt(line, idx, query.length);
    hits.push({
      score: 1,
      hit: { kind: 'docContent', docId: doc.id, line: i + 1, snippet, matchRange: range },
    });
    found++;
  }
}

function nameHit(doc: OpenDoc, query: string, hits: Hit[]): void {
  const name = doc.ref?.name ?? 'Untitled';
  if (name.toLowerCase().includes(query.toLowerCase())) {
    hits.push({ score: 3, hit: { kind: 'docName', docId: doc.id } });
  }
}

export function searchActiveDoc(activeDoc: OpenDoc | undefined, query: string): Hit[] {
  if (!activeDoc) return [];
  if (query === '') return [];
  const hits: Hit[] = [];
  if (activeDoc.kind !== 'image') nameHit(activeDoc, query, hits);
  scanContent(activeDoc, query, hits);
  return hits;
}

export function searchOpenTabs(docs: OpenDoc[], activeId: string | null, query: string): Hit[] {
  if (query === '') return [];
  const hits: Hit[] = [];
  for (const doc of docs) {
    if (doc.id === activeId) continue;
    if (doc.kind === 'image') continue;
    nameHit(doc, query, hits);
    scanContent(doc, query, hits);
  }
  return hits;
}

const MIN_WORKSPACE_QUERY = 2;

export async function searchWorkspace(
  query: string,
  roots: FileHandleRef[],
  platform: PlatformBridge,
  signal?: AbortSignal,
): Promise<Hit[]> {
  if (query.length < MIN_WORKSPACE_QUERY) return [];
  if (!platform.capabilities.search) return [];
  if (typeof platform.searchFiles !== 'function') return [];
  const rootsWithPath = roots.filter(
    (r): r is FileHandleRef & { path: string } => typeof r.path === 'string' && r.path.length > 0,
  );
  if (rootsWithPath.length === 0) return [];

  const perRoot = await Promise.all(
    rootsWithPath.map((r) => platform.searchFiles!(r.path, query, { content: true })),
  );
  if (signal?.aborted) return [];

  const seen = new Set<string>();
  const hits: Hit[] = [];
  for (const list of perRoot) {
    const q = query.toLowerCase();
    for (const h of list) {
      if (seen.has(h.path)) continue;
      seen.add(h.path);
      // Always emit a name hit when the file/dir name matches the query OR the
      // hit is a directory (directories have no content to match). This keeps
      // file/folder NAME results visible even when the same file also has
      // content matches — the row-sort puts names ahead of content.
      const nameMatches = h.name.toLowerCase().includes(q);
      if (nameMatches || h.isDir === true) {
        hits.push({
          score: 2,
          hit: { kind: 'wsName', path: h.path, name: h.name, isDir: h.isDir === true },
        });
      }
      if (h.contentMatches && h.contentMatches.length > 0) {
        for (const m of h.contentMatches) {
          hits.push({
            score: 1,
            hit: {
              kind: 'wsContent',
              path: h.path,
              name: h.name,
              line: m.line,
              snippet: m.text,
              matchRange: [0, query.length],
            },
          });
        }
      } else if (!nameMatches) {
        // SearchHit came back but neither name nor content explicitly matched —
        // surface as a name hit anyway so the file is reachable from results.
        // (Dirs already got a name hit above, so this only fires for files.)
        hits.push({
          score: 2,
          hit: { kind: 'wsName', path: h.path, name: h.name, isDir: false },
        });
      }
    }
  }
  return hits;
}
